import { IAppContext } from 'App/AppContext';

export enum LocalStorageKeys {
  NotSet = '',
  //the preferred user name of the last sign-in, the login hint of every identity provider
  UserName = 'REDLAB-Preferred-Username',
  DarkMode = 'REDLAB-DarkMode',
  LocalAccount = 'REDLAB-Local-Account',
  //an org unit on its own directory, with that directory, so a cold load can re-send the org unit header
  LastSeparateDirectoryOrgUnit = 'REDLAB-LastSeparateDirectoryOrgUnit',
  //the identity provider of the last completed sign-in; absent means Microsoft Entra ID
  LastIdentityProvider = 'REDLAB-LastIdentityProvider',
  //the allow-listed OpenID Connect issuer of the last organization sign-in (the login lookup answer as JSON), so a
  //cold load can try that issuer silently before showing the welcome screen
  LastOidcIssuer = 'REDLAB-LastOidcIssuer',
  AdminConsentParams = 'REDLAB-AdminConsentParams',
  RecentTenants = 'REDLAB-RecentTenants',
  MainNavCollapsed = 'REDLAB-MainNavCollapsed',
  //the tab of Administration > Integrations the user opened last
  AdminIntegrationsTab = 'REDLAB-AdminIntegrationsTab',
}

//
// The prefix of every name the app writes.
//
const localStorageNamePrefix = 'REDLAB-';

function getLocalStorageName(appContext: IAppContext | undefined, name: LocalStorageKeys): string {
  let storageName: string = name;
  if (appContext && appContext.user.tenant.id) {
    storageName += `-${appContext.user.tenant.id}`;
  }

  return storageName;
}

export function setLocalStorageData(
  appContext: IAppContext | undefined,
  name: LocalStorageKeys,
  value: string,
  tenantSpecific: boolean = true,
) {
  try {
    if (name === LocalStorageKeys.NotSet) return;
    let storageName: string = name;
    if (tenantSpecific) {
      storageName = getLocalStorageName(appContext, name);
    }
    window.localStorage.setItem(storageName, value);
  } catch {
    // no-op
  }
}

export function getLocalStorageData(appContext: IAppContext | undefined, name: LocalStorageKeys): string | undefined {
  try {
    if (name === LocalStorageKeys.NotSet) return;

    const storageName = getLocalStorageName(appContext, name);
    let val = window.localStorage.getItem(storageName);
    if (val === null && storageName !== name) {
      val = window.localStorage.getItem(name);
    }

    if (!val) return undefined;

    return val;
  } catch {
    return undefined;
  }
}

export function removeLocalStorageData(appContext: IAppContext | undefined, name: LocalStorageKeys) {
  try {
    if (name === LocalStorageKeys.NotSet) return;

    const storageName = getLocalStorageName(appContext, name);
    window.localStorage.removeItem(name);
    window.localStorage.removeItem(storageName);
  } catch {
    // no-op
  }
}

//
// Clears every prefixed name in the browser that the app does not write: a key taken out of
// LocalStorageKeys, and whatever an older version left behind. A name counts as known when it is a
// value of LocalStorageKeys, on its own or with the id of an organization behind it, which is how
// setLocalStorageData writes most of them. Called once at startup, so a name that has to survive a
// redirect away from the app and back belongs in the enum like any other.
//
export function removeUnknownLocalStorageData() {
  try {
    const knownNames = Object.values(LocalStorageKeys).filter((name) => name.startsWith(localStorageNamePrefix));

    const unknownNames: string[] = [];
    for (let idx = 0; idx < window.localStorage.length; idx++) {
      const name = window.localStorage.key(idx);
      if (!name || !name.startsWith(localStorageNamePrefix)) continue;
      if (!knownNames.some((known) => name === known || name.startsWith(known + '-'))) {
        unknownNames.push(name);
      }
    }

    unknownNames.forEach((name) => window.localStorage.removeItem(name));
  } catch {
    // no-op
  }
}
